/**
 * PATH: packages/email-hub/src/feature/components/AttachmentPreviewModal.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-ATTACHMENTPREVIEWMODAL-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/AttachmentPreviewModal.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/AttachmentPreviewModal.tsx:
 *     thin re-export shim that points back here.
 */

import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  Loader2,
  Pen,
  X,
} from 'lucide-react'
import { EmailHtmlBody, sanitizeEmailHtml } from '@quotech/ui'
import { cn } from '../../_internal/lib/utils'
import { emailApiFetch } from '../lib/api'
import { formatFileSize } from '../lib/utils'
import type { EmailAttachment } from '../hooks/useEmails'

// Lazy-load the PDF editor so the ~600 KB pdfjs-dist + pdf-lib
// bundle never lands on the initial chunk — operators only pull
// it down the first time they click "Sign document" on a PDF.
const PdfEditor = lazy(() => import('./PdfEditor'))

/**
 * AttachmentPreviewModal
 * ----------------------
 *
 * Lightbox-style overlay that previews email attachments inline.
 *
 * Two preview pipelines:
 *
 * 1. **Binary blob pipeline** (image, pdf, text):
 *    Browser handles these natively. We pull the raw bytes through
 *    the authenticated ``/api/email-attachments/<id>/download/``
 *    endpoint, wrap in a ``blob:`` URL, and point ``<img>`` /
 *    ``<iframe>`` / ``<pre>`` at it.
 *
 * 2. **Structured-JSON pipeline** (excel, docx, msg/eml, fallback
 *    text-via-extractor):
 *    Browser CAN'T natively render these, but we already ship the
 *    full ``ContentExtractor`` service in the backend (openpyxl,
 *    python-docx, pypdf, pytesseract, .msg-extractor). We hit the
 *    ``/api/email-attachments/<id>/preview/`` endpoint which routes
 *    through ``ContentExtractor.extract_from_bytes`` and returns:
 *
 *    .. code-block:: json
 *
 *       {
 *         "kind": "excel",
 *         "sheets": [{ "name": "Sheet1",
 *                      "rows": [["H1","H2"],["a","b"]] }],
 *         "truncated": false,
 *         ...
 *       }
 *
 *    The frontend then renders an actual sheet-tab UI (for Excel) or
 *    rich paragraph/table blocks (for Word). No half-baked
 *    "preview not available" placeholder.
 *
 * Renderer selection is in ``pickRenderer`` — MIME wins, extension
 * is the fallback because Outlook routinely ships
 * ``application/octet-stream`` for well-typed files.
 *
 * Auth: ``emailApiFetch`` injects the JWT for both endpoints. We
 * never put raw URLs into ``<iframe src>`` because they'd 401.
 *
 * The modal accepts the full attachment list + active index so the
 * user can flip between attachments with the chevrons or arrow keys.
 */

interface AttachmentPreviewModalProps {
  attachments: EmailAttachment[]
  initialIndex: number
  isOpen: boolean
  onClose: () => void
  /** Email these attachments belong to. When set, the PDF editor
   *  offers "Sign & reply" (attach the signed PDF to a reply to this
   *  email). Omitted when previewing outside a thread. */
  emailId?: string | null
}

export default function AttachmentPreviewModal({
  attachments,
  initialIndex,
  isOpen,
  onClose,
  emailId = null,
}: AttachmentPreviewModalProps) {
  const [index, setIndex] = useState(initialIndex)

  useEffect(() => { setIndex(initialIndex) }, [initialIndex, isOpen])

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight' && index < attachments.length - 1) setIndex(i => i + 1)
      if (e.key === 'ArrowLeft' && index > 0) setIndex(i => i - 1)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, index, attachments.length, onClose])

  if (!isOpen || attachments.length === 0) return null

  const current = attachments[index]
  if (!current) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Preview ${current.filename}`}
    >
      <div
        className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Header
          attachment={current}
          index={index}
          total={attachments.length}
          onPrev={() => setIndex(i => Math.max(0, i - 1))}
          onNext={() => setIndex(i => Math.min(attachments.length - 1, i + 1))}
          onClose={onClose}
        />
        <div className="flex-1 min-h-0 overflow-hidden bg-muted/20">
          <PreviewBody attachment={current} emailId={emailId} key={current.id} />
        </div>
      </div>
    </div>
  )
}

function Header({
  attachment,
  index,
  total,
  onPrev,
  onNext,
  onClose,
}: {
  attachment: EmailAttachment
  index: number
  total: number
  onPrev: () => void
  onNext: () => void
  onClose: () => void
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-border shrink-0">
      <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm text-foreground truncate" title={attachment.filename}>
          {attachment.filename}
        </p>
        <p className="text-xs text-muted-foreground">
          {(attachment.content_type || attachment.mime_type) ?? 'unknown type'}
          {attachment.size > 0 && ` · ${formatFileSize(attachment.size)}`}
          {total > 1 && ` · ${index + 1} of ${total}`}
        </p>
      </div>
      {total > 1 && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onPrev}
            disabled={index === 0}
            className="p-1.5 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
            title="Previous attachment (←)"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={index === total - 1}
            className="p-1.5 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
            title="Next attachment (→)"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
      {attachment.download_url && (
        <DownloadButton url={attachment.download_url} filename={attachment.filename} />
      )}
      <button
        type="button"
        onClick={onClose}
        className="p-1.5 rounded hover:bg-accent shrink-0"
        title="Close (Esc)"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

function DownloadButton({ url, filename }: { url: string; filename: string }) {
  const [busy, setBusy] = useState(false)
  return (
    <button
      type="button"
      disabled={busy}
      onClick={async () => {
        setBusy(true)
        try {
          const res = await emailApiFetch(url)
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          const blob = await res.blob()
          const objectUrl = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = objectUrl
          a.download = filename || 'attachment'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
        } finally {
          setBusy(false)
        }
      }}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-border rounded-md hover:bg-accent shrink-0"
      title="Download"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
      Download
    </button>
  )
}

// ---------------------------------------------------------------------------
// PreviewBody — chooses the right pipeline (binary blob vs structured
// JSON) based on renderer kind.
// ---------------------------------------------------------------------------

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'binary'; blobUrl: string; blob: Blob; text?: string }
  | { kind: 'structured'; payload: PreviewPayload }

interface PreviewPayload {
  kind: 'excel' | 'docx' | 'text' | 'pdf' | 'image' | 'unsupported'
  filename: string
  content_type: string
  size: number
  truncated: boolean
  text: string | null
  sheets: { name: string; rows: string[][] }[] | null
  blocks: (
    | { type: 'paragraph'; text: string; html?: string | null; heading?: number | null }
    | { type: 'table'; rows: string[][] }
  )[] | null
  error: string | null
}

function PreviewBody({
  attachment,
  emailId = null,
}: {
  attachment: EmailAttachment
  emailId?: string | null
}) {
  const renderer = useMemo(() => pickRenderer(attachment), [attachment])
  const [state, setState] = useState<LoadState>({ kind: 'loading' })
  const objectUrlRef = useRef<string | null>(null)
  // PDF-only: when true, render the in-browser PdfEditor instead
  // of the read-only iframe. We keep this state in PreviewBody (not
  // the modal root) so flipping attachments resets it automatically
  // — the ``key={current.id}`` prop on this component bins the old
  // editor session whenever the user navigates to a sibling
  // attachment.
  const [editingPdf, setEditingPdf] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!attachment.download_url) {
      setState({ kind: 'error', message: 'This attachment is not downloadable.' })
      return
    }

    setState({ kind: 'loading' })

    const previewUrl = previewUrlFromDownloadUrl(attachment.download_url)

    ;(async () => {
      try {
        // Structured pipeline for everything ContentExtractor can
        // make sense of (Excel, Word, .msg/.eml, generic text via
        // backend). The endpoint returns JSON, never bytes.
        if (renderer === 'excel' || renderer === 'docx' || renderer === 'extracted-text') {
          const res = await emailApiFetch(previewUrl)
          if (!res.ok) {
            const txt = await res.text().catch(() => '')
            throw new Error(`HTTP ${res.status}${txt ? ` - ${txt.slice(0, 200)}` : ''}`)
          }
          const payload: PreviewPayload = await res.json()
          if (cancelled) return
          if (payload.kind === 'unsupported') {
            setState({
              kind: 'error',
              message: payload.error || 'No preview available for this file type.',
            })
            return
          }
          setState({ kind: 'structured', payload })
          return
        }

        // Binary pipeline for image/pdf/text. Fetch as blob, hand to
        // <img>/<iframe>/<pre>.
        const res = await emailApiFetch(attachment.download_url!)
        if (!res.ok) {
          const txt = await res.text().catch(() => '')
          throw new Error(`HTTP ${res.status}${txt ? ` - ${txt.slice(0, 200)}` : ''}`)
        }
        const blob = await res.blob()
        const blobUrl = URL.createObjectURL(blob)
        objectUrlRef.current = blobUrl

        let text: string | undefined
        if (renderer === 'text') {
          text = await blob.text()
        }

        if (cancelled) {
          URL.revokeObjectURL(blobUrl)
          return
        }
        setState({ kind: 'binary', blobUrl, blob, text })
      } catch (err) {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : 'Failed to load attachment'
        setState({ kind: 'error', message: msg })
      }
    })()

    return () => {
      cancelled = true
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }
  }, [attachment.id, attachment.download_url, renderer])

  // PERF 2026-06-05 — prefetch the heavy, lazily-loaded PdfEditor chunk
  // (legacy pdf.js + pdf-lib) AND warm its worker asset (the chunk's
  // module-init fetches the worker) while the user is viewing the
  // read-only PDF preview. Without this, clicking "Sign document"
  // cold-downloaded ~1-2 MB of editor + worker on the click, leaving
  // the user on a long "Loading PDF…" spinner. Prefetching here
  // overlaps that download with their reading time so the click is
  // near-instant. Fired once the binary blob is ready; ``import()`` is
  // idempotent so the eventual ``lazy()`` resolves from cache. The
  // small delay yields to the preview's own iframe render first.
  useEffect(() => {
    if (renderer !== 'pdf' || state.kind !== 'binary') return
    const id = window.setTimeout(() => {
      import('./PdfEditor').catch(() => {
        /* prefetch is best-effort; the lazy() on click still works. */
      })
    }, 200)
    return () => window.clearTimeout(id)
  }, [renderer, state.kind])

  if (state.kind === 'loading') {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-sm">Loading attachment…</p>
        </div>
      </div>
    )
  }

  if (state.kind === 'error') {
    return (
      <div className="h-full w-full flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-3">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
          <p className="font-medium text-foreground">Couldn't load preview</p>
          <p className="text-sm text-muted-foreground break-words">{state.message}</p>
          {attachment.download_url && (
            <DownloadButton url={attachment.download_url} filename={attachment.filename} />
          )}
        </div>
      </div>
    )
  }

  if (state.kind === 'structured') {
    const { payload } = state
    if (payload.kind === 'excel' && payload.sheets) {
      return <ExcelPreview sheets={payload.sheets} truncated={payload.truncated} />
    }
    if (payload.kind === 'docx') {
      return <DocxPreview blocks={payload.blocks} text={payload.text} truncated={payload.truncated} />
    }
    if (payload.kind === 'text' && payload.text != null) {
      return <ExtractedTextPreview text={payload.text} truncated={payload.truncated} />
    }
    return (
      <UnsupportedPreview
        attachment={attachment}
        blobUrl={null}
        reason={payload.error}
      />
    )
  }

  // state.kind === 'binary'
  switch (renderer) {
    case 'image':
      return (
        <div className="h-full w-full overflow-auto flex items-center justify-center p-4">
          <img
            src={state.blobUrl}
            alt={attachment.filename}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )
    case 'pdf':
      // Two modes: read-only preview (default) and the full
      // ``PdfEditor`` overlay. In read-only mode we surface a clear
      // "Sign document" action in OUR OWN toolbar above the iframe.
      // The previous affordance was a small pill pinned bottom-right,
      // which overlapped the browser's native PDF chrome and operators
      // kept missing it ("I should be able to sign it here"). A
      // dedicated action bar can't collide with the native toolbar and
      // is always visible. Once the editor is open it owns the whole
      // pane (toolbar + scroll surface).
      if (editingPdf) {
        return (
          <Suspense
            fallback={
              <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading editor…
              </div>
            }
          >
            <PdfEditor
              pdfBytes={state.blob}
              filename={attachment.filename}
              anchorEmailId={emailId}
              onClose={() => setEditingPdf(false)}
            />
          </Suspense>
        )
      }
      return (
        <div className="flex flex-col w-full h-full">
          <div className="flex items-center gap-3 px-3 py-2 border-b border-border bg-background shrink-0">
            <span className="hidden sm:block text-xs text-muted-foreground truncate">
              Need to sign this? Add your signature, initials, name or date - no download required.
            </span>
            <button
              type="button"
              onClick={() => setEditingPdf(true)}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-semibold shadow-sm hover:bg-primary/90 shrink-0"
              title="Add your signature, initials, name or date to this PDF"
            >
              <Pen className="h-3.5 w-3.5" /> Sign document
            </button>
          </div>
          <iframe
            src={state.blobUrl}
            title={attachment.filename}
            className="flex-1 min-h-0 w-full border-0 bg-white"
          />
        </div>
      )
    case 'text': {
      const mime = (attachment.content_type || attachment.mime_type || '').toLowerCase()
      const ext = (attachment.extension || attachment.filename.split('.').pop() || '').toLowerCase()
      if ((mime === 'text/html' || ext === 'html' || ext === 'htm') && state.text) {
        return (
          <div className="h-full w-full overflow-auto bg-white p-4 dark:bg-background">
            <EmailHtmlBody html={state.text} fitToWidth />
          </div>
        )
      }
      return (
        <pre className="h-full w-full overflow-auto p-4 text-xs whitespace-pre-wrap font-mono bg-background text-foreground">
          {state.text}
        </pre>
      )
    }
    case 'unsupported':
    default:
      return <UnsupportedPreview attachment={attachment} blobUrl={state.blobUrl} />
  }
}

// ---------------------------------------------------------------------------
// Structured-payload renderers
// ---------------------------------------------------------------------------

/**
 * ExcelPreview
 * ------------
 * Tabbed sheet viewer that mirrors the look of the Quotech file
 * preview pane: sheet tabs along the top, a sticky-header table
 * filling the remaining space. We treat row 0 as the header — the
 * backend already merges headers into the row array for us.
 */
function ExcelPreview({
  sheets,
  truncated,
}: {
  sheets: { name: string; rows: string[][] }[]
  truncated: boolean
}) {
  const [activeIdx, setActiveIdx] = useState(0)

  if (sheets.length === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center p-6 text-muted-foreground text-sm">
        Spreadsheet has no readable sheets.
      </div>
    )
  }

  const active = sheets[Math.min(activeIdx, sheets.length - 1)]
  const headerRow = active.rows[0] || []
  const dataRows = active.rows.slice(1)

  return (
    <div className="h-full w-full flex flex-col">
      {sheets.length > 1 && (
        <div className="flex items-center gap-1 px-3 py-2 border-b border-border overflow-x-auto shrink-0 bg-muted/20">
          {sheets.map((s, i) => (
            <button
              key={`${s.name}-${i}`}
              type="button"
              onClick={() => setActiveIdx(i)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1 text-xs rounded-md whitespace-nowrap transition-colors',
                i === activeIdx
                  ? 'bg-primary/10 text-primary border border-primary/30'
                  : 'border border-border hover:bg-accent text-muted-foreground',
              )}
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
              {s.name || `Sheet ${i + 1}`}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full text-xs border-collapse">
          {headerRow.length > 0 && (
            <thead className="sticky top-0 bg-muted/80 backdrop-blur z-10">
              <tr>
                <th className="px-2 py-1.5 border border-border text-muted-foreground text-[10px] font-mono w-10">#</th>
                {headerRow.map((h, ci) => (
                  <th
                    key={ci}
                    className="px-3 py-1.5 border border-border text-left font-medium text-foreground"
                  >
                    {h || <span className="text-muted-foreground italic">col {ci + 1}</span>}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {dataRows.map((row, ri) => (
              <tr key={ri} className="hover:bg-accent/40">
                <td className="px-2 py-1 border border-border text-muted-foreground text-[10px] font-mono text-right bg-muted/30">
                  {ri + 2}
                </td>
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className="px-3 py-1 border border-border align-top whitespace-pre-wrap break-words max-w-md"
                  >
                    {cell}
                  </td>
                ))}
                {row.length < headerRow.length && headerRow.slice(row.length).map((_, ci) => (
                  <td
                    key={`empty-${ci}`}
                    className="px-3 py-1 border border-border bg-muted/10"
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {dataRows.length === 0 && headerRow.length > 0 && (
          <div className="p-4 text-center text-xs text-muted-foreground">
            (No data rows - this sheet only contains headers)
          </div>
        )}
      </div>

      {truncated && (
        <div className="px-3 py-2 border-t border-border text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 shrink-0">
          Preview was truncated for performance. Download the file to see the full contents.
        </div>
      )}
    </div>
  )
}

/**
 * DocxPreview
 * -----------
 * Renders the ordered ``blocks`` payload from the backend (paragraphs
 * + tables). Falls back to flat text when the block shape isn't
 * available (older payloads or extractor edge-cases). The look mimics
 * a stripped-down Word document — headings stay punchy, tables get
 * proper borders, paragraphs get comfortable line spacing.
 */
function docxParagraphTag(heading: number | null | undefined): 'h1' | 'h2' | 'h3' | 'p' {
  if (heading === 1) return 'h1'
  if (heading === 2) return 'h2'
  if (heading === 3) return 'h3'
  return 'p'
}

function docxParagraphClass(heading: number | null | undefined): string {
  if (heading === 1) return 'text-xl font-semibold whitespace-pre-wrap break-words'
  if (heading === 2) return 'text-lg font-semibold whitespace-pre-wrap break-words'
  if (heading === 3) return 'text-base font-semibold whitespace-pre-wrap break-words'
  return 'whitespace-pre-wrap break-words'
}

function DocxPreview({
  blocks,
  text,
  truncated,
}: {
  blocks: (
    | { type: 'paragraph'; text: string; html?: string | null; heading?: number | null }
    | { type: 'table'; rows: string[][] }
  )[] | null
  text: string | null
  truncated: boolean
}) {
  if ((!blocks || blocks.length === 0) && !text) {
    return (
      <div className="h-full w-full flex items-center justify-center p-6 text-muted-foreground text-sm">
        Document has no readable content.
      </div>
    )
  }

  return (
    <div className="h-full w-full overflow-auto">
      <article className="max-w-3xl mx-auto px-8 py-8 space-y-4 text-sm leading-relaxed text-foreground">
        {blocks && blocks.length > 0
          ? blocks.map((b, i) => {
              if (b.type === 'paragraph') {
                if (b.html) {
                  const Tag = docxParagraphTag(b.heading)
                  return (
                    <Tag
                      key={i}
                      className={docxParagraphClass(b.heading)}
                      dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(b.html) }}
                    />
                  )
                }
                return (
                  <p key={i} className="whitespace-pre-wrap break-words">
                    {b.text}
                  </p>
                )
              }
              return (
                <table key={i} className="w-full text-xs border-collapse my-3">
                  <tbody>
                    {b.rows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          <td
                            key={ci}
                            className="px-3 py-1.5 border border-border align-top whitespace-pre-wrap break-words"
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            })
          : (
            <pre className="whitespace-pre-wrap break-words font-sans">{text}</pre>
          )}
      </article>
      {truncated && (
        <div className="sticky bottom-0 px-3 py-2 border-t border-border text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30">
          Preview was truncated for performance. Download the file to see the full contents.
        </div>
      )}
    </div>
  )
}

/**
 * ExtractedTextPreview
 * --------------------
 * For .msg/.eml/CSV-via-extractor — renders the extracted plain text
 * with line wrap and a monospaced layout for tabular data, similar to
 * how Outlook shows .msg attachments inline.
 */
function ExtractedTextPreview({
  text,
  truncated,
}: {
  text: string
  truncated: boolean
}) {
  return (
    <div className="h-full w-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-auto">
        <pre className="p-4 text-xs whitespace-pre-wrap break-words font-mono bg-background text-foreground">
          {text}
        </pre>
      </div>
      {truncated && (
        <div className="px-3 py-2 border-t border-border text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 shrink-0">
          Preview was truncated for performance. Download to see full content.
        </div>
      )}
    </div>
  )
}

function UnsupportedPreview({
  attachment,
  blobUrl,
  reason,
}: {
  attachment: EmailAttachment
  blobUrl: string | null
  reason?: string | null
}) {
  const ext = (attachment.extension || attachment.filename.split('.').pop() || '').toLowerCase()
  const officeHints: Record<string, string> = {
    pptx: 'PowerPoint presentation',
    ppt: 'PowerPoint presentation',
  }
  const label = officeHints[ext] || `${ext.toUpperCase() || 'File'} attachment`

  return (
    <div className="h-full w-full flex items-center justify-center p-6">
      <div className="max-w-md text-center space-y-4">
        <div className={cn(
          'mx-auto h-16 w-16 rounded-2xl flex items-center justify-center',
          'bg-primary/10 text-primary',
        )}>
          <FileText className="h-8 w-8" />
        </div>
        <div className="space-y-1">
          <p className="font-medium text-foreground">{label}</p>
          <p className="text-sm text-muted-foreground">
            {reason ||
              "Inline preview isn't available for this format yet. Download " +
              'it to view in your default app, or open it in a new browser tab.'}
          </p>
        </div>
        <div className="flex items-center justify-center gap-2">
          {blobUrl ? (
            <>
              <a
                href={blobUrl}
                download={attachment.filename}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-md hover:bg-accent"
              >
                <Download className="h-4 w-4" /> Download
              </a>
              <a
                href={blobUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-md hover:bg-accent"
              >
                <ExternalLink className="h-4 w-4" /> Open in new tab
              </a>
            </>
          ) : attachment.download_url ? (
            <DownloadButton url={attachment.download_url} filename={attachment.filename} />
          ) : null}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// pickRenderer + helpers
// ---------------------------------------------------------------------------

type RendererKind =
  | 'image'
  | 'pdf'
  | 'text'           // browser-native text (txt/csv/json/log/etc.)
  | 'excel'          // structured backend extract
  | 'docx'           // structured backend extract
  | 'extracted-text' // backend extract (msg/eml/etc.)
  | 'unsupported'

const TEXT_EXTENSIONS = new Set([
  'txt', 'csv', 'json', 'log', 'md', 'xml', 'yaml', 'yml',
  'html', 'htm', 'css', 'js', 'ts', 'tsx', 'jsx',
])

const EXCEL_EXTENSIONS = new Set(['xls', 'xlsx', 'xlsm', 'xlsb'])
const WORD_EXTENSIONS = new Set(['doc', 'docx'])
const EXTRACTED_TEXT_EXTENSIONS = new Set(['msg', 'eml'])

function pickRenderer(att: EmailAttachment): RendererKind {
  const mime = (att.content_type || att.mime_type || '').toLowerCase()
  const ext = (att.extension || att.filename.split('.').pop() || '').toLowerCase()

  if (mime.startsWith('image/') || att.is_image) return 'image'
  if (mime === 'application/pdf' || att.is_pdf || ext === 'pdf') return 'pdf'

  if (
    mime.includes('spreadsheet') ||
    mime.includes('excel') ||
    EXCEL_EXTENSIONS.has(ext)
  ) {
    return 'excel'
  }
  if (
    mime.includes('wordprocessingml') ||
    mime === 'application/msword' ||
    WORD_EXTENSIONS.has(ext)
  ) {
    return 'docx'
  }
  if (
    mime === 'message/rfc822' ||
    mime === 'application/vnd.ms-outlook' ||
    EXTRACTED_TEXT_EXTENSIONS.has(ext)
  ) {
    return 'extracted-text'
  }

  if (mime.startsWith('text/') || TEXT_EXTENSIONS.has(ext)) return 'text'
  return 'unsupported'
}

/**
 * Derive the structured-preview URL from the binary download URL.
 *
 * The backend sets a stable contract: every attachment exposes both
 * ``/api/email-attachments/<id>/download/`` and
 * ``/api/email-attachments/<id>/preview/``. Rather than have the
 * serializer ship two URLs (and double the payload), we just swap
 * the trailing segment client-side.
 */
function previewUrlFromDownloadUrl(downloadUrl: string): string {
  return downloadUrl.replace(/\/download\/?$/, '/preview/')
}

/**
 * AttachmentPreviewBody
 * ---------------------
 * The modal's inner preview pipeline (binary blob → iframe/img/pre,
 * structured → excel/docx/text) exposed WITHOUT the lightbox chrome so
 * other surfaces can render an attachment INLINE instead of in a popup.
 *
 * Operator (2026-06-02): on the invoice-candidate review split they want
 * to "view the invoice instead of clicking attachment and a pop up". The
 * candidate review's Bill tab mounts this directly in its pane — same
 * auth-gated fetch, same renderers, no modal. The host owns the height
 * (this fills `h-full w-full`).
 *
 * It is the SAME component the modal renders (`key={att.id}` is the
 * host's responsibility when swapping attachments), so the two stay in
 * lock-step — no drift, no duplicated fetch logic.
 */
export { PreviewBody as AttachmentPreviewBody }
